import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { PrismicRichText } from '@prismicio/react';

import { prismicFieldHasContent } from '../prismic/helpers';

import { contentfulFieldHasContent, assetExists, getEntryContentType } from './helpers';

// Receives a Contentful asset entry and returns Prismic compatible image format
export function imageEntryToPrismicFormat(imageAssetEntry) {
  if (!imageAssetEntry) {
    return undefined;
  }

  if (!assetExists(imageAssetEntry)) {
    return undefined;
  }

  // Already in desired format
  if (imageAssetEntry.dimensions) {
    return imageAssetEntry;
  }

  let alt = '';
  let url = '';
  let width = 0;
  let height = 0;

  if (imageAssetEntry.fields) {
    const {
      fields: {
        file: { url: rawUrl },
      },
    } = imageAssetEntry;

    url = rawUrl;

    // Contentful SDK URL does not contain protocol so it starts at //
    if (/^\/\//.test(url)) {
      url = `https:${url}`;
    }

    alt = imageAssetEntry.fields.description;
    width = imageAssetEntry.fields.file.details.image.width;
    height = imageAssetEntry.fields.file.details.image.height;
  } else {
    alt = imageAssetEntry.description;
    url = imageAssetEntry.url;
    width = imageAssetEntry.width;
    height = imageAssetEntry.height;
  }

  return { url, alt, dimensions: { width, height } };
}

// Receives a Contentful entry and returns Prismic compatible meta data to be used in the document meta tags
export function getDocumentFromSEOMetadata(entry) {
  if (!Object.prototype.hasOwnProperty.call(entry.fields, 'seoMetadata')) {
    const {
      sys: {
        id,
        contentType: {
          sys: { id: contentType },
        },
      },
    } = entry;
    throw new Error(
      `Contentful document ${id} (${contentType}) is not set up to use "seoMetadata" or GraphQL query is not requesting "seoMetadata" field. View instructions on how to add "seoMetadata" here https://www.notion.so/remotecom/Contentful-Guides-5708b7461a554b1e95be845b703cafc8#934d09de48aa4404b4b50815481b80b4`
    );
  }

  const {
    fields: { seoMetadata },
  } = entry;

  const {
    fields: { title, description, keywords, image, noIndex, noFollow },
  } = seoMetadata;

  return {
    seo_title: title,
    seo_description: description,
    social_image: imageEntryToPrismicFormat(image),
    noIndex,
    noFollow,

    // Not in use yet
    keywords,
  };
}

// Receives a Contentful entry and returns Prismic compatible meta data
export function getEntryMeta(entry) {
  const {
    sys: { locale: lang },
    fields: { slug: uid } = {},
    slug,
  } = entry;

  return {
    type: getEntryContentType(entry),
    uid: slug || uid,
    lang,
  };
}

export function isContentfulRichText(field) {
  return field?.nodeType === 'document' || field?.json?.nodeType === 'document';
}

export function universalFieldHasContent(field) {
  if (isContentfulRichText(field)) {
    return contentfulFieldHasContent(field);
  }
  return prismicFieldHasContent(field);
}

export function RenderFieldText({ field }) {
  if (isContentfulRichText(field)) {
    let fieldContent = field;
    if (field?.json) {
      fieldContent = field?.json;
    }
    return documentToReactComponents(fieldContent);
  }
  if (typeof field === 'object') {
    return <PrismicRichText field={field} />;
  }
  return field;
}
